import type { ColumnsType } from 'antd/es/table';
import { Tag } from 'antd';

import {
  checkTypeLabels,
  checkStatusLabels,
  decisionLabels,
  type CheckType,
  type CheckStatus,
  type Decision,
  type MedicalCheckRow,
  type UpcomingAction,
} from '@/entities/medical/model/medicalChecksSchema';
import { medicalCheckStatusTagColor, medicalAdmissionTagColor } from '@/entities/medical/model/medicalCheckTableTags';

export const medicalEmployeeCheckColumns: ColumnsType<MedicalCheckRow> = [
  {
    title: 'Тип',
    dataIndex: 'check_type',
    key: 'type',
    width: 170,
    render: (v: string) => checkTypeLabels[v as CheckType] ?? v,
  },
  {
    title: 'Статус',
    dataIndex: 'status',
    key: 'status',
    width: 140,
    render: (v: string) => (
      <Tag color={medicalCheckStatusTagColor[v] ?? 'default'}>{checkStatusLabels[v as CheckStatus] ?? v}</Tag>
    ),
  },
  {
    title: 'Решение',
    dataIndex: 'decision',
    key: 'decision',
    width: 180,
    render: (v: string | null) =>
      v ? <Tag color={medicalAdmissionTagColor[v] ?? 'default'}>{decisionLabels[v as Decision] ?? v}</Tag> : '—',
  },
  {
    title: 'Дата прохождения',
    dataIndex: 'checked_at',
    key: 'checked',
    width: 140,
    render: (v: string | null) => v ?? '—',
  },
  {
    title: 'Следующий',
    dataIndex: 'next_check_date',
    key: 'next',
    width: 130,
    render: (v: string | null) => v ?? '—',
  },
];

export const medicalEmployeeActionColumns: ColumnsType<UpcomingAction> = [
  {
    title: 'Действие',
    dataIndex: 'title',
    key: 'title',
    render: (v: string) => {
      const raw = String(v ?? '').trim();
      if (!raw) return '—';
      const [left] = raw.split(':', 1);
      const typeKey = (left ?? raw).trim();
      return checkTypeLabels[typeKey as CheckType] ?? typeKey;
    },
  },
  {
    title: 'Срок',
    dataIndex: 'due_date',
    key: 'due',
    width: 130,
    render: (v: string | null) => v ?? '—',
  },
  {
    title: 'Статус',
    dataIndex: 'status',
    key: 'status',
    width: 140,
    render: (v: string) => (
      <Tag color={medicalCheckStatusTagColor[v] ?? 'default'}>{checkStatusLabels[v as CheckStatus] ?? v}</Tag>
    ),
  },
];

export function computeMedicalEmployeeStats(checks: MedicalCheckRow[] | undefined) {
  if (!checks) return { overdue: 0, upcoming: 0, inProgress: 0, passed: 0, total: 0 };

  const today = new Date();
  const horizon30 = new Date(today);
  horizon30.setDate(horizon30.getDate() + 30);

  const overdue = checks.filter(
    (c) => c.status === 'overdue' || c.status === 'failed' || c.status === 'not_passed',
  ).length;
  const upcoming = checks.filter((c) => {
    if (c.status === 'overdue' || c.status === 'failed' || c.status === 'not_passed') return false;
    if (!c.next_check_date) return false;
    const d = new Date(c.next_check_date);
    return d > today && d <= horizon30;
  }).length;
  const inProgress = checks.filter((c) => c.status === 'assigned' || c.status === 'in_progress').length;
  const passed = checks.filter((c) => c.status === 'passed').length;
  return { overdue, upcoming, inProgress, passed, total: checks.length };
}
